import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { setLoading } from "../../redux/ducks/Loading";
import { useTranslation } from "react-i18next";
import { Col, Input, Modal, Row, Select, Spin, Table, Tooltip } from "antd";
import {
  PlusOutlined,
  SaveOutlined,
  DownloadOutlined,
  CheckOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import Button from "../../components/functional_components/Button/PFButton";
import { cloneDeep, get, includes, isEmpty, isNaN, omit, remove, toLower } from "lodash";
import {
  buildQueryString,
  checkRoles,
  exportFile,
  formatNumber,
  showError,
  showSuccess,
  trimData,
  unformatNumber,
  checkTimesheetDecimals,
  decimalValueInCent,
} from "../../utils/utilities";
import { langMonth } from "../../utils/properties";
import { useFormik } from "formik";
import dayjs from "dayjs";
import "./TimesheetTable.css";
import TimesheetPortal from "../../services/api/TimesheetPortal";
import ExportTimesheet from "../../services/api/ExportTimesheet";

const { Option } = Select;
const { confirm } = Modal;

const TimesheetTable = (props) => {
  const params = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userRoles = useSelector((state) => get(state.userInfoDuck, "userInfo.roles", {}));
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const [state, setState] = useState({
    customForm: [],
    dataTable: [],
    startData: [],
    closed: false,
    columns: [],
    loading: false,
    adjLoading: false,
    projectsList: [],
    activityList: [],
    activityListAdj: {},
    isDirty: false,
    modal: {
      visible: false,
      type: "",
      data: {},
    },
    adjModal: {
      visible: false,
      type: "",
      data: {
        projectTaskMainId: "",
      },
    },
    columnCounter: 0,
    rowForm: [{ project: null, activity: null }],
    errors: false,
    actualFilterDate: props.filterData,
    decimalError: [],
    adjustError: [],
    totalActivityCheck: false,
    oldParamsId: params.id,
    alert24h: [],
    note: "",
    latestDayWithApprovedHours: 0,
    errorDaySum: [], // days before today where total hours timesheet !== total presence hours (updated onChange)
    squaredDays: [], // days before lastDaySentToApprove where the hours sum is now ok but were reopened due to a squaring (can be edited until they are resent for approval) plus days before today where there are squared hours blocks
    shouldCheckAdp: false,
  });
  let year = null;
  let month = null;
  // Verifica e converte filterData
  const filterDate = typeof props.filterData === "string" ? dayjs(props.filterData, "YYYYMM") : props.filterData;
  // Ottieni anno e mese
  if (filterDate.isValid()) {
    year = filterDate.year();
    month = String(filterDate.month() + 1).padStart(2, "0"); // Ottenere il mese (da 0 a 11, quindi +1),padStart per aggiungere uno zero (es. 01, 02).
  }

  const dayInMonth = (year, month) => {
    // how many day have this specificy month
    return new Date(year, month, 0).getDate();
  };

  const today = dayjs();

  useEffect(() => {
    if (state.actualFilterDate !== props.filterData || state.oldParamsId !== params.id) {
      //this control is for render ok
      getData(props.filterData);
      setState((prevState) => ({ ...prevState, actualFilterDate: props.filterData, oldParamsId: params.id }));
    } else if (state.columnCounter > 0) {
      //this control is for render ok
      getColumns();
    } else {
      getData();
    }
  }, [props.filterData, state.columnCounter, currentLanguage, params.id, state.dataTable.errorTimesheetTotal]);

  const getData = async (month = "", updateActivity = false) => {
    dispatch(setLoading(true));
    const data = await TimesheetPortal.TimesheetData(
      params.id // I pass the cid in querystring only if I am editing another employee's timesheet
        ? month !== "" // month is for check if i am in the first
          ? `month=${month}&cid=${params.id}&updateActivity=${updateActivity}` // updateActivity is for check if i delete the last activty, BE not give me the activty of last month
          : `cid=${params.id}&updateActivity=${updateActivity}`
        : month !== ""
          ? `month=${month}&updateActivity=${updateActivity}`
          : `updateActivity=${updateActivity}`
    );
    if (!data.error) {
      if (data.data.value) {
        let totalActivityCheck = false;
        let error = [];
        const cForm = {};
        data.data.value.forEach((item, i) => {
          cForm[i] = item.hours; // I create a custom form
          Object.keys(item.hours).forEach((h, index) => {
            if (item.hours[h] % 1 !== 0) {
              // have decimals?
              const decimals = item.hours[h].toString().split(".");
              if (decimals.length === 2) {
                // means that i have decimals
                error = checkTimesheetDecimals(decimals[1], i, index, error, true); // function return errors if decimal are diffrent of ["00", "15", "30", "45"]
              }
            }
          });
          item.projectDescription = `${item.projectId} | ${item.projectName}`; // name of project
          item.projectTaskDescription = `${item.projectTaskId} | ${item.projectTaskName}`; // name of project task
          item.key = i; // This is necessary to remove warning in console for unique key row and I use it for open row table
          if (item.total === 0 && month !== "") {
            totalActivityCheck = true; // if total is 0 and I am at the start of the new month (I am approving the whole past month), I won't send an empty activity
          }
        });
        data.data.totalValues.forEach((item, i) => {
          item.key = `total-${i}`; // This is necessary to remove warning in console for unique key row and I use it for open row table
        });
        data.data.totalValues[0].projectDescription = "TIMESHEET_TOTAL";
        data.data.totalValues[1].projectDescription = "PRESENCE_TOTAL";
        const latestDayWithApprovedHoursGlobal = Math.max(...data.data.value.map((item) => item.lastDayApproved)) || 0; // get the latest day with approved hours before adding the total values
        data.data.value.push(data.data.totalValues[0]); // add TIMESHEET_TOTAL line
        data.data.value.push(data.data.totalValues[1]); // add PRESENCE_TOTAL line

        // calculate initial error day sums
        const errorDaySum = [];
        if (data.data.checkAdp) {
          Object.entries(data.data.totalValues[0].hours).forEach(([day, hours]) => {
            if (
              (props.currentMonth < Number(today.format("YYYYMM")) || day < today.date()) &&
              Math.abs(hours - data.data.totalValues[1].hours[day]) > 0.01
            ) {
              errorDaySum.push(Number(day));
            }
          });
        }
        setState((prevState) => ({
          ...prevState,
          dataTable: data.data,
          startData: cloneDeep(data.data),
          closed: data.data.closed,
          columnCounter: state.columnCounter + 1,
          totalActivityCheck,
          customForm: cForm,
          decimalError: error,
          adjustError: [],
          alert24h: [],
          latestDayWithApprovedHours: latestDayWithApprovedHoursGlobal,
          errorDaySum,
          shouldCheckAdp: data.data.checkAdp,
        }));
      }
      if (data.data.prevMonthValues && data.data.value.length < 3 && props.filterData == props.currentMonth) {
        prevMonthValues();
      }
    } else {
      showError(get(data, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const canDeleteActivity = (data) => {
    if (data.status === "REFUTED") {
      return (
        !state.closed &&
        (data.total === 0 ||
          !data.dayStatus ||
          // eslint-disable-next-line no-unused-vars
          !Object.entries(data.dayStatus).some(([_, status]) => status === "APPROVED" || status === "WAITING_APPROVAL"))
      );
    }
    return ((!data.lastDayApproved && !data.lastDaySentToApprove) || data.total === 0) && !state.closed;
  };

  const calculateSquaredDays = () => {
    const squaredDays = [];
    if (state.shouldCheckAdp) {
      state.dataTable.value.forEach((item) => {
        if (item.dayStatus) {
          Object.entries(item.dayStatus).forEach(([day, status]) => {
            if (
              // the hours have been modified after a squaring
              (status === "CREATED" &&
                item.status !== "CREATED" &&
                day <= state.dataTable.lastDaySentToApprove &&
                !squaredDays.includes(Number(day))) ||
              // the sum of the hours is wrong for days after lastDaySentToApprove and before today
              ((props.currentMonth < Number(today.format("YYYYMM")) || day < today.date()) &&
                status === "SQUARING" &&
                !squaredDays.includes(Number(day)))
            ) {
              squaredDays.push(Number(day));
            }
          });
        }
      });
    }
    setState((prevState) => ({ ...prevState, squaredDays }));
    return squaredDays;
  };

  const getColumns = () => {
    const dayNumbers = dayInMonth(year, month); // how many day have this month?
    let myDate = new Date(); // set date
    myDate.setFullYear(year); //set year
    myDate.setMonth(month - 1); //set month
    const squaredDays = calculateSquaredDays();
    const objColData = [
      {
        title: t("timesheet.project"),
        dataIndex: "projectDescription",
        width: 200,
        fixed: "left",
        className: "center subTitleTimesheet timesheetColumns",
        onCell: (record) => {
          let className = "";
          if (record.valueType === "TIMESHEET_TOTAL" || record.valueType === "PRESENCE_TOTAL") {
            className = "backgoundTotalTimesheet";
          }
          if (record.blocked === true) {
            className = `${className} blocked`;
          }
          switch (record.type) {
            case "CANCEL":
              className = `${className} backgroundCancel`;
              break;
            case "ADJUST":
              className = `${className} backgroundAdjust`;
              break;
          }
          return {
            className: className,
            colSpan: record.valueType === "TIMESHEET_TOTAL" || record.valueType === "PRESENCE_TOTAL" ? 2 : 1,
          };
        },
        render: (val) =>
          val === "TIMESHEET_TOTAL"
            ? t("timesheet.timesheetTotal")
            : val === "PRESENCE_TOTAL"
              ? t("timesheet.presenceTotal")
              : val,
      },
      {
        title: t("timesheet.activity"),
        dataIndex: "projectTaskDescription",
        width: 200,
        fixed: "left",
        className: "center subTitleTimesheet timesheetColumns",
        onCell: (record) => {
          let className = "";
          if (record.valueType === "TIMESHEET_TOTAL" || record.valueType === "PRESENCE_TOTAL") {
            className = "backgoundTotalTimesheet";
          }
          if (record.blocked === true) {
            className = `${className} blocked`;
          }
          switch (record.type) {
            case "CANCEL":
              className = `${className} backgroundCancel`;
              break;
            case "ADJUST":
              className = `${className} backgroundAdjust`;
              break;
          }
          return {
            className: className,
            colSpan: record.valueType === "TIMESHEET_TOTAL" || record.valueType === "PRESENCE_TOTAL" ? 0 : 1,
          };
        },
      },
    ];

    const getCellStatus = (record, dayNumber) => {
      if (squaredDays.includes(dayNumber)) {
        return "SQUARING";
      }

      const lastDaySentToApprove = state.dataTable.lastDaySentToApprove || 0;
      // use SQUARING for hours without day status (0.00 hours) that were already sent for approval, but now their day has adp hours <> timesheet hours
      if (
        dayNumber <= lastDaySentToApprove &&
        state.dataTable.errorTimesheetTotal &&
        state.dataTable.errorTimesheetTotal.includes(dayNumber)
      ) {
        return "SQUARING";
      }

      // use day status if present
      if (record.dayStatus && record.dayStatus[dayNumber]) {
        // // Commented because this case is checked in calculateSquaredDays and is caught by the first if
        // if (record.dayStatus[dayNumber] === "CREATED" && dayNumber <= record.lastDaySentToApprove) {
        //   // if a past hours block that didn't exist has been created because the day was squared, show it as squared
        //   return "SQUARING";
        // }
        return record.dayStatus[dayNumber];
      }

      if (dayNumber <= record.lastDayApproved) {
        return "APPROVED"; // show hours in days preceding or equal to the latest approved hours, as approved (for example, for activities created later or for past blocks with zero hours)
      } else if (dayNumber <= lastDaySentToApprove) {
        // in days after the latest approval and before or equal to the latest day with hours sent for approval, or if no hours of this record have ever been sent for approval:
        // if the record (activity) status is not CREATED, use the activity status for the day (REFUTED, WAITING_APPROVAL or APPROVED)
        // if the record status is CREATED, if the day considered is before or equal to the last day approved globally, show as approved, else show as waiting approval
        // const lastDayApprovedGlobal = state.dataTable.lastDayApproved || 0;
        return record.status;
      } else {
        return "CREATED";
      }
    };

    const getDisplayType = (record, dayNumber) => {
      if (checkRoles(userRoles, ["SYSTEM_IT"]) && (record.type === "CANCEL" || record.type === "ADJUST")) {
        return "input";
      }

      const cellStatus = getCellStatus(record, dayNumber);
      if (
        state.closed ||
        cellStatus === "WAITING_APPROVAL" ||
        cellStatus === "APPROVED" ||
        record.valueType === "TIMESHEET_TOTAL" ||
        record.valueType === "PRESENCE_TOTAL" ||
        record.blocked === true
      ) {
        return "text";
      } else {
        // cellStatus is CREATED, REFUTED or SQUARING (timesheet is open, record is not a total)
        return "input";
      }
    };

    const getCellClassnames = (record, dayNumber) => {
      if (!record.dayStatus && !record.status) {
        return "";
      }

      let className = "";
      const cellStatus = getCellStatus(record, dayNumber);
      switch (cellStatus) {
        case "APPROVED":
          className = "timesheetStatusApproved";
          break;
        case "WAITING_APPROVAL":
          className = "timesheetStatusWaitingApproval";
          break;
        case "REFUTED":
          className = "timesheetStatusRefuted";
          break;
        case "SQUARING":
          if (
            record.dayStatus[dayNumber] === "SQUARING" &&
            record.status === "APPROVED" &&
            record.lastDayApproved &&
            dayNumber <= record.lastDayApproved
          ) {
            // the hours had been approved but the day is now squared
            className = "timesheetStatusSquaringApproved timesheetStatusSquaring";
          } else {
            // the hours have been modified after a squaring
            className = "timesheetStatusSquaring";
          }
          break;
      }
      return className;
    };

    for (let i = 1; i <= dayNumbers; i++) {
      // all day of month
      myDate.setDate(i); // set date
      objColData.push({
        title: i,
        dataIndex: ["hours", i],
        width: 100,
        className:
          myDate.getDay() == 6 || myDate.getDay() == 0
            ? "table-value-right highlighted"
            : "table-value-right timesheetColumns", //is week-end?
        render: (amount, record) => {
          return formatNumber({
            value: amount,
            lang: currentLanguage,
            displayType: getDisplayType(record, i),
            decimalScale: 2,
            customInput: checkRoles(userRoles, ["SYSTEM_IT"]) ? Input : record.blocked === true ? null : Input,
            onChange: (e) => handleChange(e, i, record.key),
            isAllowed: ({ floatValue }) => floatValue <= 24, // max value is 24 h
            onFocus: (e) => handleFocus(e, record.key), // onFocus input become null for ui/ux
          });
        },
        onCell: (record) => {
          let className = "";
          if (includes(state.alert24h, i)) {
            // if on save BE response (one day total (column) > 24h)
            className = "borderRed";
          }
          if (
            includes(checkDecimalErrors(), `${i}-${record.key}`) ||
            includes(state.adjustError, `${i}-${record.key}`)
          ) {
            className = "borderRed"; // if there is something wrong in input (error of decimal or error of adjust hours by system it)
          }
          if (record.valueType === "TIMESHEET_TOTAL" || record.valueType === "PRESENCE_TOTAL") {
            className = `${className} backgoundTotalTimesheet`;
          }
          if (record.blocked === true) {
            className = `${className} blocked`;
          }
          if (includes(state.dataTable.errorTimesheetTotal, i) || includes(state.errorDaySum, i)) {
            // if BE see that presence total and timesheet total are not equals
            className = `${className} PFred`;
          }
          switch (record.type) {
            case "CANCEL":
              className = `${className} backgroundCancel`;
              break;
            case "ADJUST":
              className = `${className} backgroundAdjust`;
              break;
          }
          className = className + " " + getCellClassnames(record, i);
          return { className };
        },
      });
    }
    objColData.push(
      {
        title: t("timesheet.total"),
        dataIndex: "total",
        width: 100,
        fixed: "right",
        className: "table-value-right timesheetColumns",
        render: (amount) =>
          formatNumber({
            value: amount,
            lang: currentLanguage,
            decimalScale: 2,
          }),
        onCell: (record) => {
          let className = "";
          if (record.valueType === "TIMESHEET_TOTAL" || record.valueType === "PRESENCE_TOTAL") {
            className = "backgoundTotalTimesheet";
          }
          if (record.blocked === true) {
            className = `${className} blocked`;
          }
          if (!isEmpty(state.dataTable.errorTimesheetTotal) || !isEmpty(state.errorDaySum)) {
            // if BE see that presence total and timesheet total are not equals
            className = `${className} PFred`;
          }
          if (
            record.total === 0 &&
            !(record.valueType === "TIMESHEET_TOTAL" || record.valueType === "PRESENCE_TOTAL" || record.type !== "MAIN")
          ) {
            className = `${className} PFred`;
          }
          switch (record.type) {
            case "CANCEL":
              className = `${className} backgroundCancel`;
              break;
            case "ADJUST":
              className = `${className} backgroundAdjust`;
              break;
          }
          return { className: className };
        },
      },
      // {
      //   title: t("timesheet.status"),
      //   dataIndex: "status",
      //   width: 140,
      //   fixed: "right",
      //   className: "table-value-center timesheetColumns",
      //   render: (val) => {
      //     switch (val) {
      //       case "CREATED":
      //         return t("timesheet.created");
      //       case "APPROVED":
      //         return t("timesheet.approved");
      //       case "REFUTED":
      //         return t("timesheet.refuted");
      //       case "WAITING_APPROVAL":
      //         return t("timesheet.waitingApproval");
      //       default:
      //         break;
      //     }
      //   },
      //   onCell: (record) => {
      //     let className = "";
      //     if (record.valueType === "TIMESHEET_TOTAL" || record.valueType === "PRESENCE_TOTAL") {
      //       className = "backgoundTotalTimesheet";
      //     }
      //     if (record.blocked === true) {
      //       className = `${className} blocked`;
      //     }
      //     switch (record.type) {
      //       case "CANCEL":
      //         className = `${className} backgroundCancel`;
      //         break;
      //       case "ADJUST":
      //         className = `${className} backgroundAdjust`;
      //         break;
      //     }
      //     return { className: className };
      //   },
      // },
      {
        title: t("general.actions"),
        fixed: "right",
        width: 100,
        className: "table-value-right timesheetColumns",
        render: (data) => {
          if (!(data.valueType === "TIMESHEET_TOTAL" || data.valueType === "PRESENCE_TOTAL")) {
            return (
              <div key={data.id} className={"justify-center"}>
                {data.status === "REFUTED" && (
                  <Tooltip
                    placement="right"
                    title={
                      <div>
                        <p>{`Note: ${data.note}`}</p>
                      </div>
                    }
                  >
                    <InfoCircleOutlined className="icon-small mr-10 PFred" />
                  </Tooltip>
                )}
                {checkRoles(userRoles, ["SYSTEM_IT"]) && data.type === "MAIN" && (
                  <div
                    className="mr-10"
                    onClick={
                      data.status === "APPROVED" && props.filterData == props.currentMonth
                        ? reopenApproval(data.id)
                        : null
                    }
                  >
                    <RollbackOutlined
                      className={
                        data.status === "APPROVED" && props.filterData == props.currentMonth
                          ? "icon-small"
                          : "icon-small noCursorPointer disabled"
                      }
                    />
                  </div>
                )}
                {checkRoles(userRoles, ["SYSTEM_IT"]) && data.type === "MAIN" && (
                  <div
                    className="mr-10"
                    onClick={
                      state.dataTable.closed && data.status === "APPROVED" && props.filterData < props.currentMonth
                        ? openAdjModal(data.projectTaskId, data.noteAdjust)
                        : null
                    }
                  >
                    <EditOutlined
                      className={
                        state.dataTable.closed && data.status === "APPROVED" && props.filterData < props.currentMonth
                          ? "icon-small"
                          : "icon-small noCursorPointer disabled"
                      }
                    />
                  </div>
                )}
                {data.type !== "CANCEL" ? (
                  <div
                    onClick={() =>
                      data.type === "ADJUST"
                        ? handleDelete(data.id, data.total, data.key)
                        : canDeleteActivity(data)
                          ? handleDelete(data.id, false, data.key)
                          : null
                    }
                  >
                    <DeleteOutlined
                      className={
                        data.type === "ADJUST" || canDeleteActivity(data)
                          ? "icon-small"
                          : "icon-small noCursorPointer disabled"
                      }
                    />
                  </div>
                ) : (
                  <div>
                    {includes(state.alert24h, data.id) && (
                      <WarningOutlined className="PFred icon-small noCursorPointer mr-10" />
                    )}
                    <Tooltip
                      placement="right"
                      title={
                        <div>
                          <p>{`Note: ${data.note}`}</p>
                        </div>
                      }
                    >
                      <InfoCircleOutlined className="icon-small" />
                    </Tooltip>
                  </div>
                )}
              </div>
            );
          } else {
            if (!isEmpty(state.dataTable.errorTimesheetTotal) || !isEmpty(state.errorDaySum)) {
              // if BE see that presence total and timesheet total are not equals
              return (
                <div key={data.id} className="justify-center">
                  <WarningOutlined className={"icon-small noCursorPointer PFred"} />
                </div>
              );
            }
            if (data.valueType === "TIMESHEET_TOTAL" && !isEmpty(state.alert24h)) {
              // if on save BE response (one day total (column) > 24h)
              return (
                <div key={data.id} className="justify-center">
                  <WarningOutlined className={"icon-small noCursorPointer PFred"} />
                </div>
              );
            }
          }
        },
        onCell: (record) => {
          let className = "";
          if (record.valueType === "TIMESHEET_TOTAL" || record.valueType === "PRESENCE_TOTAL") {
            className = "backgoundTotalTimesheet";
          }
          if (record.blocked === true) {
            className = `${className} blocked`;
          }
          switch (record.type) {
            case "CANCEL":
              className = `${className} backgroundCancel`;
              break;
            case "ADJUST":
              className = `${className} backgroundAdjust`;
              break;
          }
          return { className: className };
        },
      }
    );
    setState((prevState) => ({ ...prevState, columns: objColData }));
  };

  const checkDecimalErrors = () => {
    let error = cloneDeep(state.decimalError);
    Object.values(state.customForm).forEach((item, index) => {
      // my custom form is crate only with obj row of obj day:hours
      Object.values(item).forEach((e, i) => {
        if (e % 1 !== 0) {
          const decimals = e.toString().split(".");
          if (decimals.length === 2) {
            // means that i have decimals
            error = checkTimesheetDecimals(decimals[1], i, index, error, true); // function return errors if decimal are diffrent of ["00", "15", "30", "45"]
          }
        }
      });
    });
    return error;
  };

  const handleFocus = (e, row) => {
    if (state.dataTable.value) {
      state.dataTable.value.forEach((item, i) => {
        if (item.hours) {
          if (i === row) {
            e.target.value = null;
          }
        }
      });
    }
  };

  const handleChange = (e, day, row) => {
    const value = e.target.value;
    const decimals = currentLanguage === "GB" ? value.split(".") : value.split(",");
    let error = cloneDeep(state.decimalError);
    let adjustError = cloneDeep(state.adjustError);
    state.adjustError.forEach((e) => {
      if (isNaN(e)) {
        adjustError.push(e);
      }
    });
    if (decimals.length === 2) {
      // means that i have decimals
      error = checkTimesheetDecimals(decimals[1], day, row, error); // function return errors if decimal are diffrent of ["00", "15", "30", "45"]
    }
    if (state.dataTable.value) {
      let sumHoursDay = 0;
      const errorDaySum = state.errorDaySum;
      state.dataTable.value.forEach((item, i) => {
        let mainValue = 0;
        let cancelValue = 0;
        let adjustValue = 0;
        let errorsValue = null;
        if (item.type === "CANCEL") {
          if (item.hours) {
            if (i === row) {
              item.hours[day] =
                unformatNumber(e.target.value, currentLanguage) <= 0
                  ? unformatNumber(e.target.value, currentLanguage)
                  : -unformatNumber(e.target.value, currentLanguage);
              cancelValue =
                unformatNumber(e.target.value, currentLanguage) <= 0
                  ? unformatNumber(e.target.value, currentLanguage)
                  : -unformatNumber(e.target.value, currentLanguage);
              mainValue = state.dataTable.value[i - 1].hours[day];
              for (
                let index = i + 1;
                state.dataTable.value[index] && state.dataTable.value[index].type !== "MAIN";
                index++
              ) {
                // I scroll through the lines to add the adjust values
                if (state.dataTable.value[index].type === "ADJUST") {
                  adjustValue = adjustValue + decimalValueInCent(state.dataTable.value[index].hours[day]);
                }
              }
              mainValue = mainValue % 1 !== 0 ? decimalValueInCent(mainValue) : mainValue;
              cancelValue = cancelValue % 1 !== 0 ? decimalValueInCent(cancelValue) : cancelValue;
              if (adjustValue + cancelValue === 0 && mainValue + cancelValue >= 0) {
                // squarings adjustment
                // ok
                remove(adjustError, (item) => {
                  return item === `${day}-${row}`;
                });
              } else {
                // not good
                adjustError.push(`${day}-${row}`);
              }
            }
          }
        } else if (item.type === "ADJUST") {
          if (item.hours) {
            if (i === row) {
              item.hours[day] = unformatNumber(e.target.value, currentLanguage);
              for (
                let index = i;
                state.dataTable.value[index] && state.dataTable.value[index].type !== "MAIN";
                index--
              ) {
                // I scroll through the lines on back to add the adjust values
                if (state.dataTable.value[index].type === "ADJUST") {
                  adjustValue = adjustValue + decimalValueInCent(state.dataTable.value[index].hours[day]);
                }
                if (state.dataTable.value[index].type === "CANCEL") {
                  mainValue = state.dataTable.value[index - 1].hours[day];
                  cancelValue = state.dataTable.value[index].hours[day];
                  errorsValue = `${day}-${index}`;
                }
              }
              for (
                let index = i + 1;
                state.dataTable.value[index] && state.dataTable.value[index].type !== "MAIN";
                index++
              ) {
                // I scroll through the lines to add the adjust values
                if (state.dataTable.value[index].type === "ADJUST") {
                  adjustValue = adjustValue + decimalValueInCent(state.dataTable.value[index].hours[day]);
                }
              }
              mainValue = mainValue % 1 !== 0 ? decimalValueInCent(mainValue) : mainValue;
              cancelValue = cancelValue % 1 !== 0 ? decimalValueInCent(cancelValue) : cancelValue;
              if (adjustValue + cancelValue === 0 && mainValue + cancelValue >= 0) {
                // squarings adjustment
                // ok
                remove(adjustError, (item) => {
                  return item === errorsValue;
                });
              } else {
                // not good
                adjustError.push(errorsValue);
              }
            }
          }
        } else {
          if (item.hours) {
            if (i === row) {
              item.hours[day] = unformatNumber(e.target.value, currentLanguage);
            }
          }
        }

        if (props.currentMonth < Number(today.format("YYYYMM")) || day < today.date()) {
          if (item.valueType === "TIMESHEET_PROJ_TASK" && item.hours) {
            sumHoursDay += item.hours[day];
            if (sumHoursDay % 1 >= 0.599) {
              // if the sum of the fractions of hour is >= 60 minutes, renormalize the fraction by moving 60 minutes into the hours count
              sumHoursDay = Math.round(sumHoursDay * 100) / 100 + 1.0 - 0.6;
            }
          } else if (item.valueType === "TIMESHEET_TOTAL" && item.hours) {
            // update timesheet total
            item.hours[day] = sumHoursDay;
          } else if (item.valueType === "PRESENCE_TOTAL" && item.hours) {
            if (Math.abs(sumHoursDay - item.hours[day]) > 0.01 && state.shouldCheckAdp) {
              if (!errorDaySum.includes(day)) {
                errorDaySum.push(day);
              }
            } else {
              remove(errorDaySum, (item) => {
                return item === day;
              });
            }
          }
        }
      });
      setState((prevState) => ({
        ...prevState,
        errorDaySum,
      }));
    }
    if (state.startData.value !== state.dataTable.value) {
      // this is for REFRESH onChange
      setState((prevState) => ({
        ...prevState,
        columnCounter: state.columnCounter + 1,
        decimalError: error,
        adjustError,
        isDirty: true,
      }));
    }
  };

  const handleSave = async () => {
    dispatch(setLoading(true));
    const data = [];
    let projectMain = "";
    state.dataTable.value.forEach((item) => {
      if (item.type === "MAIN") {
        projectMain = item.projectTaskMainId;
      }
      if (item.projectTaskId) {
        let obj = {
          projectTaskId: item.projectTaskId,
          projectTaskMainId: projectMain,
          type: item.type,
          hours: item.hours,
          dayStatus: item.dayStatus,
          id: item.id,
        };
        data.push(obj);
      }
    });
    const save = await TimesheetPortal.saveTimesheet(props.filterData, params.id ? `cid=${params.id}` : "", data); // I pass the cid in querystring only if I am editing another employee's timesheet
    if (!save.error) {
      if (isEmpty(save)) {
        showSuccess();
        getData(props.filterData);
      }
      setState((prevState) => ({
        ...prevState,
        alert24h: save,
        columnCounter: state.columnCounter + 1,
        isDirty: false,
      }));
    } else {
      showError(get(save, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const handleRelease = async () => {
    confirm({
      title: t("timesheet.releaseMessage"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        dispatch(setLoading(true));
        const approve = await TimesheetPortal.approveTimesheet(props.filterData, params.id ? `cid=${params.id}` : ""); // I pass the cid in querystring only if I am editing another employee's timesheet
        if (!approve.error) {
          showSuccess();
          getData(props.filterData);
        } else {
          showError(get(approve, "error.messageCode", "ER000"));
        }
        dispatch(setLoading(false));
      },
      onCancel() {},
    });
  };

  const handleDownload = async () => {
    // download 2 file
    dispatch(setLoading(true));
    const queryString = buildQueryString(
      {},
      { month: props.filterData, cid: params.id ? params.id : state.dataTable.userCid }
    ); // I pass the cid in querystring only if I am editing another employee's timesheet
    const file = await ExportTimesheet.downloadTimesheet(queryString.string);
    const fileCheckAdp = await ExportTimesheet.downloadCheckAdp(queryString.string);
    if (!file.error && !fileCheckAdp.error) {
      const blob = new Blob([file]);
      exportFile(blob, `Timesheet-${`${month}_${year}`}.csv`);
      const blobCheck = new Blob([fileCheckAdp]);
      exportFile(blobCheck, "checkAdp.csv");
      showSuccess();
    } else {
      showError(get(file, "error.messageCode", "ER000"));
      showError(get(fileCheckAdp, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const handleDelete = (id, total, index) => {
    let counter = 0;
    for (let i = index; state.dataTable.value[i] && state.dataTable.value[i].type !== "MAIN"; i++) {
      // I scroll through the lines to increment counter
      if (state.dataTable.value[i].type === "ADJUST") {
        counter = counter + 1;
      }
    }
    for (let i = index - 1; state.dataTable.value[i] && state.dataTable.value[i].type !== "CANCEL"; i--) {
      if (state.dataTable.value[i].type === "ADJUST") {
        counter = counter + 1;
      }
    }
    if (total !== 0 && state.closed && counter !== 1) {
      // we need the counter because if i have the last adjustment row i can delete the row without message
      showError(t("timesheet.deleteTotalAdj")); // "To be able to eliminate it is necessary that the total of the activity is 0"
    } else {
      confirm({
        title: t("general.ensureDelete"),
        content: state.isDirty ? t("timesheet.notSaveMessage") : "",
        icon: <ExclamationCircleOutlined />,
        onOk: async () => {
          const deleteResponse = await TimesheetPortal.deleteLineTimesheet(id, params.id ? `cid=${params.id}` : ""); // I pass the cid in querystring only if I am editing another employee's timesheet
          if (!deleteResponse.error) {
            showSuccess();
            getData(props.filterData);
          } else {
            showError(get(deleteResponse, "error.messageCode", "ER000"));
          }
        },
        onCancel() {},
      });
    }
  };

  const prevMonthValues = () => {
    confirm({
      title: t("timesheet.prevMonthValues"), // "Dear user, do you want to copy the activities of the previous month?"
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        getData(props.filterData, true);
      },
      onCancel() {},
    });
  };

  const formikTimesheet = useFormik({
    // this formik is for add normal activity
    initialValues: {
      project: null,
      activity: null,
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.activity) {
        errors.activity = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      dispatch(setLoading(true));
      const data = await TimesheetPortal.addRowTimesheet(
        props.filterData,
        values.activity,
        params.id ? `cid=${params.id}` : ""
      ); // I pass the cid in querystring only if I am editing another employee's timesheet
      if (!data.error) {
        getData(props.filterData);
        setState((prevState) => ({ ...prevState, columnCounter: state.columnCounter + 1 }));
        showSuccess();
      } else {
        showError(get(data, "error.messageCode", "ER000"));
      }
      dispatch(setLoading(false));
      closeModal();
    },
  });

  const closeModal = () => {
    formikTimesheet.resetForm();
    setState((prevState) => ({ ...prevState, modal: { visible: false, type: "", data: {} } }));
  };

  const openModal = () => {
    if (state.isDirty) {
      confirm({
        title: t("timesheet.notSaveMessage"),
        icon: <ExclamationCircleOutlined />,
        onOk: async () => {
          setState((prevState) => ({ ...prevState, loading: true }));
          getProjectList();
          getProjectTaskList("", 0, false);
          setState((prevState) => ({ ...prevState, loading: false, modal: { visible: true, type: "new", data: {} } }));
        },
        onCancel() {},
      });
    } else {
      setState((prevState) => ({ ...prevState, loading: true }));
      getProjectList();
      getProjectTaskList("", 0, false);
      setState((prevState) => ({ ...prevState, loading: false, modal: { visible: true, type: "new", data: {} } }));
    }
  };

  const getProjectList = async () => {
    const project = await TimesheetPortal.listProject(params.id ? `cid=${params.id}` : ""); // I pass the cid in querystring only if I am editing another employee's timesheet
    if (!project.error) {
      setState((prevState) => ({
        ...prevState,
        projectsList: get(project, "data", []),
      }));
    }
    return project;
  };

  const getProjectTaskList = async (projectId = "", i = 0, isAdj = true) => {
    let activityListAdj = state.activityListAdj;
    let activityList = state.activityList;
    const queryString = buildQueryString(
      {},
      { projectId: projectId !== "" ? projectId : "", cid: params.id ? params.id : "" }
    ); // I pass the cid in querystring only if I am editing another employee's timesheet
    const projectTask = await TimesheetPortal.listProjectTask(queryString.string);
    if (!projectTask.error) {
      if (isAdj) {
        activityListAdj[i] = get(projectTask, "data", []);
      } else {
        activityList = get(projectTask, "data", []);
      }
      setState((prevState) => ({
        ...prevState,
        activityListAdj,
        activityList,
      }));
    }
    return projectTask;
  };

  const submitAdjForm = async () => {
    dispatch(setLoading(true));
    let data = [];
    state.rowForm.forEach((item) => data.push(item.activity));
    let error = false;
    for (var i = 0; i < state.rowForm.length; i++) {
      for (var j = 0; j < state.rowForm.length; j++) {
        if (i != j) {
          if (state.rowForm[i].activity == state.rowForm[j].activity) {
            error = true; // means there are duplicate values
          }
        }
      }
    }
    if (error || includes(data, state.adjModal.data.projectTaskMainId)) {
      showError("ER004");
    } else {
      let objData = {
        note: state.note,
        projectTaskAdjustIds: data,
      };
      const save = await TimesheetPortal.addRowAdjTimesheet(
        props.filterData,
        state.adjModal.data.projectTaskMainId,
        params.id ? `cid=${params.id}` : "",
        objData
      ); // I pass the cid in querystring only if I am editing another employee's timesheet
      if (!save.error) {
        getData(props.filterData);
      } else {
        showError(get(save, "error.messageCode", "ER000"));
      }
      dispatch(setLoading(false));
      closeAdjModal();
    }
  };

  const closeAdjModal = () => {
    setState((prevState) => ({
      ...prevState,
      adjModal: { visible: false, type: "", data: {} },
      rowForm: [{ project: null, activity: null }],
      errors: false,
    }));
  };

  const openAdjModal = (projectTaskId, note) => () => {
    if (state.isDirty) {
      confirm({
        title: t("timesheet.notSaveMessage"),
        icon: <ExclamationCircleOutlined />,
        onOk: async () => {
          setState((prevState) => ({ ...prevState, adjLoading: true }));
          getProjectList();
          getProjectTaskList();
          setState((prevState) => ({
            ...prevState,
            adjLoading: false,
            adjModal: {
              visible: true,
              type: "new",
              data: { projectTaskMainId: projectTaskId, note: note },
            },
            note: note,
          }));
        },
        onCancel() {},
      });
    } else {
      setState((prevState) => ({ ...prevState, adjLoading: true }));
      getProjectList();
      getProjectTaskList();
      setState((prevState) => ({
        ...prevState,
        adjLoading: false,
        adjModal: {
          visible: true,
          type: "new",
          data: { projectTaskMainId: projectTaskId, note: note },
        },
        note: note,
      }));
    }
  };

  const addAdjRow = () => {
    const rows = cloneDeep(state.rowForm);
    rows.push({ project: null, activity: null });
    getProjectTaskList("", rows.length - 1);
    setState((prevState) => ({ ...prevState, rowForm: rows, errors: false }));
  };

  const deleteAdjRow = (index) => {
    const errors = [];
    const rows = cloneDeep(state.rowForm);
    remove(rows, (_, i) => {
      return i === index;
    });
    const removeItem = omit(state.activityListAdj, index);
    let newAdjList = {};
    Object.keys(removeItem).forEach((k, i) => {
      newAdjList[i] = removeItem[k];
    });
    rows.forEach((item, i) => {
      // check error
      if (isEmpty(item.activity)) {
        errors.push(i);
      }
    });
    setState((prevState) => ({
      ...prevState,
      rowForm: rows,
      activityListAdj: newAdjList,
      errors: isEmpty(errors) ? true : false,
    }));
  };

  const handleAdjChange = (i, e, name) => {
    const errors = [];
    let newFormValues = [...state.rowForm]; // rowForm is my custom form for adjustment change
    if (name === "project") {
      newFormValues[i]["activity"] = null;
      newFormValues[i][name] = e;
      getProjectTaskList(e, i);
    } else {
      newFormValues[i][name] = e;
    }
    newFormValues.forEach((item, i) => {
      if (isEmpty(item.activity)) {
        errors.push(i);
      }
    });
    setState((prevState) => ({ ...prevState, rowForm: newFormValues, errors: isEmpty(errors) ? true : false }));
  };

  const handleFiletrProjectChange = (e) => {
    formikTimesheet.setFieldValue("project", e);
    formikTimesheet.setFieldValue("activity", null);
    getProjectTaskList(e, 0, false);
  };

  const reopenApproval = (id) => async () => {
    confirm({
      title: t("timesheet.reopenApproval.titleModal"),
      content: t("timesheet.reopenApproval.descriptionModal"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        const deleteResponse = await TimesheetPortal.reopenApprovalTimesheet(id, params.id ? `cid=${params.id}` : ""); // I pass the cid in querystring only if I am editing another employee's timesheet
        if (!deleteResponse.error) {
          showSuccess();
          getData(props.filterData);
        } else {
          showError(get(deleteResponse, "error.messageCode", "ER000"));
        }
      },
      onCancel() {},
    });
  };

  return (
    <>
      <Row align="middle" justify="space-between" className="mb-10">
        <Col style={{ display: "flex", flexDirection: "row" }}>
          <div className="legend timesheetColumns">{t("timesheet.legend.created")}</div>
          <div className="legend timesheetStatusWaitingApproval">{t("timesheet.legend.waitingApproval")}</div>
          <div className="legend timesheetStatusApproved">{t("timesheet.legend.approved")}</div>
          <div className="legend timesheetStatusRefuted">{t("timesheet.legend.refuted")}</div>
          <div className="legend timesheetStatusSquaring">{t("timesheet.legend.squaring")}</div>
        </Col>
        <Col style={{ display: "flex", flexDirection: "row" }}>
          <Button
            currentIcon={<PlusOutlined className="icon-medium" />}
            clickCallback={openModal}
            isDisabled={state.dataTable.closed}
          />
          <Button
            isLoading={state.downloadLoading}
            currentIcon={<DownloadOutlined className="icon-medium" />}
            clickCallback={handleDownload}
            className={"ml-10"}
          />
        </Col>
      </Row>
      {get(state.dataTable, "value.length", 1) < 3 ? (
        <Row type="flex" justify="center" className="mb-10 mt-10 fw-bold">
          {t("timesheet.emptyState")}
        </Row>
      ) : (
        <>
          <Row type="flex" justify="start" className="headerTimesheet">
            <Col className="headerName headerCentered">
              {t("timesheet.employee")}
              {`: ${state.dataTable.userCid} | ${state.dataTable.userName}`}
            </Col>
            <Col className="headerName">
              <Row>
                {t("timesheet.cdcService")}
                {`: ${state.dataTable.costCentreId}`}
              </Row>
              <Row>
                {t("timesheet.service")}
                {`: ${state.dataTable.serviceId}`}
              </Row>
            </Col>
            <Col className="headerMonth headerCentered">
              {t("timesheet.timesheet")}
              {`: ${
                dayjs.isDayjs(props.filterData)
                  ? props.filterData.format(langMonth[toLower(currentLanguage)])
                  : dayjs(props.filterData, "YYYYMM").format(langMonth[toLower(currentLanguage)])
              }`}
            </Col>
          </Row>
          <Table
            bordered
            columns={state.columns}
            dataSource={state.dataTable.value}
            pagination={false}
            scroll={{ x: 1500 }}
            rowHoverable={false}
          />

          <Row type="flex" aling="middle" justify="space-between" className="mb-10 mt-10">
            <Col span={12}>
              <div className="boxValue">
                {state.dataTable.lastUpdateDateADP === null
                  ? t("timesheet.notSyncro")
                  : `${t("timesheet.lastSyncro")}: ${dayjs(state.dataTable.lastUpdateDateADP).format(
                      currentLanguage === "GB" ? "YYYY/MM/DD, hh:mm:ss a" : "DD/MM/YYYY, HH:mm:ss"
                    )}`}
              </div>
            </Col>

            <Col md={12}>
              {!isEmpty(state.decimalError) && (
                <Row className="PFred flex-end mb-10">
                  <WarningOutlined className="PFred icon-medium noCursorPointer mr-10" />
                  {t("timesheet.warningDecimal")}
                </Row>
              )}
              {!isEmpty(state.adjustError) && (
                <Row className="PFred flex-end mb-10">
                  <WarningOutlined className="PFred icon-medium noCursorPointer mr-10" />
                  {t("timesheet.alertAdjust")}
                </Row>
              )}
              {!isEmpty(state.alert24h) && (
                <Row className="PFred flex-end mb-10">
                  <WarningOutlined className="PFred icon-medium noCursorPointer ml-10 mr-10" />
                  {t("timesheet.alert24h")}
                </Row>
              )}
            </Col>
          </Row>
          <Row type="flex" aling="middle" justify="end" className="mb-10 mt-10">
            <Col span={12} offset={12}>
              <Row>
                <Col span={12} className="right">
                  <Button
                    isDisabled={
                      !state.isDirty ||
                      !isEmpty(state.decimalError) ||
                      !isEmpty(state.adjustError) ||
                      !isEmpty(state.errorDaySum)
                    }
                    currentIcon={<SaveOutlined className="icon-medium" />}
                    clickCallback={handleSave}
                  />
                  <span>
                    <b>{t("timesheet.save")}</b>
                  </span>
                </Col>
                <Col span={12} className="right">
                  {!state.closed && (
                    <>
                      <Button
                        isDisabled={
                          state.isDirty ||
                          !state.dataTable.canRelease ||
                          state.totalActivityCheck ||
                          !isEmpty(state.dataTable.errorTimesheetTotal) ||
                          !isEmpty(state.decimalError) ||
                          !isEmpty(state.alert24h) ||
                          state.dataTable.value?.some((item) => item.status === "WAITING_APPROVAL") ||
                          !isEmpty(state.errorDaySum)
                        }
                        currentIcon={<CheckOutlined className="icon-medium" />}
                        clickCallback={handleRelease}
                      />
                      <span>
                        <b>{t("timesheet.approve")}</b>
                      </span>
                    </>
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
        </>
      )}

      <Modal
        width={700}
        title={<b>{t("timesheet.adjTimesheet")}</b>}
        open={state.modal.visible}
        onCancel={closeModal}
        footer={[
          <Button
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikTimesheet.submitForm}
            isDisabled={!(formikTimesheet.dirty && formikTimesheet.isValid)}
          />,
        ]}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Row gutter={[40, 48]}>
          <Col lg={12} md={12} xs={24} sm={12}>
            <Row>
              <label className="fw-bold mb-10 mt-10" htmlFor="project">
                {t("timesheet.project")}
              </label>
            </Row>
            <Row>
              <Select
                showSearch
                placeholder={t("timesheet.project")}
                id="project"
                name="project"
                className="w-100"
                onBlur={formikTimesheet.handleBlur("project")}
                onChange={(e) => handleFiletrProjectChange(e)}
                value={formikTimesheet.values.project}
              >
                {state.projectsList.map((item, i) => (
                  <Option key={i} value={item.id} name={item.id}>{`${item.id} | ${item.name}`}</Option>
                ))}
              </Select>
            </Row>
          </Col>
          <Col lg={12} md={12} xs={24} sm={12}>
            <Row>
              <label className="fw-bold mb-10 mt-10" htmlFor="activity">
                {t("timesheet.activity")}*
              </label>
            </Row>
            <Row>
              <Select
                className="w-100"
                id="activity"
                name="activity"
                placeholder={t("timesheet.activity")}
                onChange={formikTimesheet.handleChange("activity")}
                onBlur={formikTimesheet.handleBlur("activity")}
                value={formikTimesheet.values.activity}
              >
                {state.activityList.map((item, i) => (
                  <Option key={i} value={item.id} name={item.id}>{`${item.id} | ${item.name}`}</Option>
                ))}
              </Select>
            </Row>
          </Col>
        </Row>
      </Modal>

      <Modal
        width={700}
        title={<b>{t("timesheet.adjTimesheet")}</b>}
        open={state.adjModal.visible}
        onCancel={closeAdjModal}
        footer={[
          <Button
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={submitAdjForm}
            isDisabled={state.adjModal.data.note ? !(state.note !== "") : !(state.errors && !isEmpty(state.note))}
          />,
        ]}
      >
        {state.adjLoading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.adjLoading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Col>
          <Row>
            <label className="fw-bold mb-10 mt-10" htmlFor={`note`}>
              {t("timesheet.note")}*
            </label>
          </Row>
          <Row>
            <Input
              placeholder={t("timesheet.note")}
              id={`note`}
              name={`note`}
              className="w-100"
              onChange={(e) => setState((prevState) => ({ ...prevState, note: e.target.value }))}
              value={state.note}
            ></Input>
          </Row>
        </Col>
        {state.rowForm.map((_, i) => {
          return (
            <Row key={i} gutter={[40, 48]}>
              <Col lg={11} md={11} xs={22} sm={11}>
                <Row>
                  <label className="fw-bold mb-10 mt-10" htmlFor={`project`}>
                    {t("timesheet.project")}
                  </label>
                </Row>
                <Row>
                  <Select
                    showSearch
                    placeholder={t("timesheet.project")}
                    id={`project`}
                    name={`project`}
                    className="w-100"
                    onChange={(e) => handleAdjChange(i, e, "project")}
                    value={state.rowForm[i].project}
                  >
                    {state.projectsList.map((item, i) => (
                      <Option key={i} value={item.id} name={item.id}>{`${item.id} | ${item.name}`}</Option>
                    ))}
                  </Select>
                </Row>
              </Col>
              <Col lg={11} md={11} xs={22} sm={11}>
                <Row>
                  <label className="fw-bold mb-10 mt-10" htmlFor={`activity`}>
                    {t("timesheet.activity")}*
                  </label>
                </Row>
                <Row>
                  <Select
                    className="w-100"
                    id={`activity`}
                    name={`activity`}
                    placeholder={t("timesheet.activity")}
                    onChange={(e) => handleAdjChange(i, e, "activity")}
                    value={state.rowForm[i].activity}
                  >
                    {state.activityListAdj[i] &&
                      state.activityListAdj[i].map((item, i) => (
                        <Option key={i} value={item.id} name={item.id}>{`${item.id} | ${item.name}`}</Option>
                      ))}
                  </Select>
                </Row>
              </Col>
              {state.rowForm.length > 1 && (
                <Col lg={1} md={1} xs={1} sm={1}>
                  <Row className="mt-2em">
                    <div className="mt-10" onClick={() => deleteAdjRow(i)}>
                      <DeleteOutlined className={"icon-small mt-10"} />
                    </div>
                  </Row>
                </Col>
              )}
            </Row>
          );
        })}
        <Row className="mt-1em justify-center">
          <div className="buttonAddActivity" onClick={addAdjRow}>
            {t("timesheet.addActivity")}
          </div>
        </Row>
      </Modal>
    </>
  );
};

export default TimesheetTable;
